<template>
  <b-container
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end mb-0 pb-0">
      <b-breadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>
    <b-container fluid>
      <b-row>
        <b-col
          cols="12"
          md="8"
          class="px-1"
          order="2"
          order-md="1"
        >
          <b-form-group
            label="Search"
            label-for="filter_search"
            label-class="font-weight-bolder"
          >
            <b-input
              id="filter_search"
              v-model="tableDisbursement.filter.search"
              type="text"
              size="sm"
              placeholder="search here"
              debounce="1000"
              autocomplete="off"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
          class="px-1"
          order="1"
          order-md="2"
        >
          <b-form-group
            label="Channel"
            label-for="filter_channel"
            label-class="font-weight-bolder"
          >
            <b-form-select
              id="filter_channel"
              v-model="tableDisbursement.filter.channel"
              size="sm"
              :options="tableDisbursement.filterOptions.channels"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -- select channel here --
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
          class="px-1"
          order="1"
          order-md="2"
        >
          <b-form-group
            label="Completed At"
            label-for="filter_date"
            label-class="font-weight-bolder"
          >
            <b-input
              id="filter_date"
              v-model="tableDisbursement.filter.date"
              size="sm"
              type="date"
              :max="tableDisbursement.filter.date"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          class="px-1 mb-2"
          order="3"
        >
          <b-button
            size="sm"
            type="button"
            variant="primary"
            class="mr-1"
            :disabled="tableDisbursement.busy || exporter.busy"
            @click="tableDisbursementRefresh"
          >
            <b-icon
              icon="arrow-clockwise"
              :animation="(tableDisbursement.busy ? 'spin' : null)"
            /> {{ tableDisbursement.busy ? 'Loading' : 'Reload' }}
          </b-button>
          <b-overlay
            rounded
            opacity="0.1"
            spinner-small
            class="d-inline"
            spinner-variant="warning"
            :show="exporter.busy"
          >
            <json-excel
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              :class="(tableDisbursement.busy || exporter.busy || tableDisbursement.filter.date === null ? `btn btn-success btn-sm disabled` : `btn btn-success btn-sm`)"
              :fetch="excelDisbursementProvider"
              :before-generate="onStartExcelExport"
              :before-finish="onFinishExcelExport"
              :fields="exporter.excel.fields"
              :escape-csv="exporter.excel.escape"
              :type="exporter.excel.type"
              :header="exporter.excel.header"
              :footer="exporter.excel.footer"
              :name="excelFileName"
              worksheet="Disbursements"
            >
              {{ exporter.busy ? 'Exporting' : 'Export As Excel' }}
            </json-excel>
          </b-overlay>
        </b-col>

      </b-row>

      <b-row class="mt-1">
        <b-col
          cols="12"
          class="px-1"
        >
          <b-table
            ref="tableDisbursement"
            hover
            small
            striped
            bordered
            responsive
            show-empty
            :stacked="isMobile()"
            :per-page="tableDisbursement.perPage"
            :current-page="tableDisbursement.currentPage"
            :items="tableDisbursementProvider"
            :fields="tableDisbursement.fields"
            :sort-by.sync="tableDisbursement.sortBy"
            :sort-desc.sync="tableDisbursement.sortDesc"
            :sort-direction="tableDisbursement.sortDirection"
            :filter="tableDisbursement.filter"
            :filter-included-fields="tableDisbursement.filterOn"
            :busy="tableDisbursement.busy"
          >
            <template #cell(index)="{ index }">
              {{ tableNumber(tableDisbursement, index) }}
            </template>

            <template #cell()="{ value }">
              <div class="text-wrap text-md-nowrap">
                {{ value }}
              </div>
            </template>

            <template #cell(active)="{ value }">
              {{ Number(value) === 1 ? 'Yes' : 'No' }}
            </template>

            <template #cell(amount)="{ value }">
              <div class="text-right">
                {{ numberFormatter(value) }}
              </div>
            </template>

            <template #cell(fee)="{ item }">
              <div class="text-right">
                {{ numberFormatter(parseFloat(item.fee) + parseFloat(item.percentage_fee)) }}
              </div>
            </template>

            <template #cell(total)="{ item }">
              <div class="text-right">
                {{ numberFormatter(parseFloat(item.amount) + (parseFloat(item.fee) + parseFloat(item.percentage_fee))) }}
              </div>
            </template>

            <template #cell(action)="row">
              <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
                <b-button
                  v-if="row.item.user_payload"
                  size="sm"
                  :variant="row.detailsShowing ? 'danger' : 'primary' "
                  @click="row.toggleDetails"
                >
                  {{ row.detailsShowing ? 'Hide' : 'Show' }} Payload
                </b-button>
              </div>
            </template>

            <template #row-details="{ item }">
              <b-form-group
                label="Payload"
                label-class="font-weight-bolder"
                class="mb-2 px-5"
              >
                <vue-json-pretty
                  :data="item.user_payload"
                />
              </b-form-group>
            </template>

            <template #table-busy>
              <div class="text-center py-5">
                <b-icon
                  icon="stopwatch"
                  font-scale="5"
                  animation="cylon"
                />
                <p class="h3 py-2">
                  <strong>Loading . . .</strong>
                </p>
              </div>
            </template>

          </b-table>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mt-1 mb-2 px-1"
        >
          <b-select
            v-model="tableDisbursement.perPage"
            :options="tableDisbursement.pageOptions"
            size="sm"
            class="w-100 w-md-25"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
        >
          <b-pagination
            v-model="tableDisbursement.currentPage"
            pills
            last-number
            first-number
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
            :total-rows="tableDisbursement.totalRows"
            :per-page="tableDisbursement.perPage"
          />
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
// import QUERY from 'lodash'
import { core } from '@/config/pluginInit'
import { SMerchantDisbursement } from '@/services'
import formatter from '@/mixins/formatter'
import Ripple from 'vue-ripple-directive'
import misc from '@/mixins/misc'
import createDate from 'moment'

export default {
  name: 'MerchantDisbursements',

  middleware: ['auth', 'merchant'],

  directives: {
    Ripple
  },

  mixins: [misc, formatter],

  metaInfo: () => ({
    title: 'Disbursements'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      tableDisbursement: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: null,
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: null,
          channel: 'All',
          date: createDate().format('YYYY-MM-DD')
        },
        filterOptions: {
          channels: ['All', 'Instapay', 'Pesonet'],
          statuses: ['All', 'Pending', 'Completed']
        },
        filterOn: [],
        busy: false,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 14, key: 'action', class: 'text-center min-w-150px' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeFormatter, sortable: true },
          { mobile: 2, key: 'partner_reference', class: 'min-w-350px' },
          { mobile: 3, key: 'channel', class: 'min-w-125px' },
          { mobile: 5, key: 'bank_name', class: 'min-w-250px' },
          { mobile: 6, key: 'account_number', class: 'min-w-250px' },
          { mobile: 7, key: 'account_name', class: 'min-w-250px' },
          { mobile: 8, key: 'amount', class: 'min-w-150px', formatter: this.numberFormatter },
          { mobile: 9, key: 'fee', class: 'min-w-150px', formatter: this.numberFormatter },
          { mobile: 10, key: 'total', class: 'min-w-150px' },
          { mobile: 11, key: 'status', class: 'text-center min-w-100px' },
          { mobile: 12, key: 'reference_number', class: 'min-w-350px' },
          { mobile: 13, key: 'completed_at', class: 'min-w-150px', formatter: this.dateTimeFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      },
      exporter: {
        busy: false,
        pdf: null,
        excel: {
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            '#': {
              field: 'page_number'
            },
            'Created At': {
              field: 'created_at',
              callback: createdAt => `="${this.dateTimeFormatter(createdAt)}"`
            },
            'Partner Reference': {
              field: 'partner_reference'
            },
            Channel: {
              field: 'channel'
            },
            'Bank Code': {
              field: 'bank_code'
            },
            'Bank Name': {
              field: 'bank_name'
            },
            'Account Number': {
              field: 'account_number'
            },
            'Account Name': {
              field: 'account_name'
            },
            Amount: {
              field: 'amount',
              callback: amount => this.numberFormatter(amount) || 0.00
            },
            Fee: {
              field: '',
              callback: ({ fee, percentage_fee }) => this.numberFormatter(parseFloat(fee) + parseFloat(percentage_fee)) || 0.00
            },
            Total: {
              field: '',
              callback: ({ amount, fee, percentage_fee }) => this.numberFormatter(parseFloat(amount) + (parseFloat(fee) + parseFloat(percentage_fee))) || 0.00
            },
            Status: {
              field: 'status'
            },
            'Reference Number': {
              field: 'reference_number'
            },
            'Completed At': {
              field: 'completed_at',
              callback: completedAt => `="${this.dateTimeFormatter(completedAt)}"`
            }
          },
          footer: []
        }
      }
    }
  },

  computed: {
    excelFileName () {
      return `Disbursements-${this.dateFormatter(this.tableDisbursement.filter.date)}`
    }
  },

  mounted () {
    core.index()
  },

  methods: {

    async tableDisbursementProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableDisbursement.busy = true
      return await SMerchantDisbursement.getReports(
        this.objectToUrl({
          page: currentPage,
          per_page: perPage,
          sort: sortBy,
          sort_desc: sortDesc,
          filter_text: filter.search,
          filter_channel: filter.channel,
          filter_completed_at: filter.date
        })
      ).then(({ data }) => {
        this.tableDisbursement.totalRows = data.total_rows
        return data.items
      }).catch(() => []).finally(() => {
        this.tableDisbursement.busy = false
      })
    },

    tableDisbursementRefresh () {
      this.$refs.tableDisbursement.refresh()
    },

    async excelDisbursementProvider () {
      if (this.exporter.busy || this.tableDisbursement.busy || this.tableDisbursement.filter.date === null) {
        return null
      }
      this.exporter.busy = true
      return await SMerchantDisbursement.getExports(
        this.objectToUrl({
          filter_text: this.tableDisbursement.filter.search,
          filter_channel: this.tableDisbursement.filter.channel,
          filter_completed_at: this.tableDisbursement.filter.date
        })
      ).then(({ data }) => {
        if (data.items.length <= 0) {
          this.swalInvalid(
            'No available data to be Exported. Try changing the date.',
            'Notice'
          )
        }
        return data.items.map((item, i) => {
          item.page_number = Number(i) + 1
          return item
        })
      }).catch(() => []).finally(() => {
        this.exporter.busy = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || this.tableDisbursement.busy || this.tableDisbursement.filter.date === null) {
        return null
      }

      this.exporter.excel.header = [
        'Disbursements',
        `As of ${this.dateShortFormatter(this.tableDisbursement.filter.date)}`,
        '&nbsp;'
      ]
    },

    onFinishExcelExport () {
    }
  }
}
</script>
